import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import {
  type HeroImageV1,
  type RestSixtyFortyColumnModal,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { useCardRenderEnvironment } from '@bbkFunnelComponents/card-render-environment';
import {
  CloseOfferButton,
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestSixtyFortyColumnModal;
  detailedAction: React.ReactElement;
  close: () => void;
};

const CopyContent = styled.div`
  overflow-y: auto;
  padding-top: 10px;
  padding-bottom: 10px;
`;

const RenderSixtyFortyColumnV2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      hero,
      info: { title: titleP, details },
    },
    mobileLayout,
  },
  detailedAction,
  close,
}) => {
  const { mobileView } = useMediaQueries();

  const { previewCanvas } = useCardRenderEnvironment();

  const imgUrl = (hero as HeroImageV1).url;

  const title = <TitleTheme data-part="title" title={titleP} />;
  const copy = <CustomerHtml value={details} />;

  const mobileViewContent = (
    <ModalMobileInnerSpacer
      className={cs('display-grid', 'sixty-forty', 'mobile', 'inner-spacer')}
    >
      <OfferTitleMobile title={title} close={close} />
      <img
        alt=""
        src={imgUrl}
        data-part="image"
        css={css`
          width: 100%;
          height: 100%;
          max-height: 300px;
          object-fit: cover;
          object-position: center;
          border-radius: 4px;
        `}
      />
      <CopyContent data-part="description">{copy}</CopyContent>
      <div>{detailedAction}</div>
    </ModalMobileInnerSpacer>
  );

  if (previewCanvas?.device === 'mobile') {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {mobileViewContent}
      </MobileDevicePreview>
    );
  }

  if (mobileView) return mobileViewContent;

  return (
    <ModalDesktopInnerSpacer
      css={css`
        padding: 0;
      `}
      className={cs('sixty-forty', 'display-grid', 'desktop', 'inner-spacer')}
    >
      <img
        data-part="image"
        alt=""
        src={imgUrl}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        `}
      />
      <div
        css={css`
          padding: 1rem;
        `}
        className={cs('display-grid', 'content-grid')}
      >
        <CloseOfferButton
          onClick={close}
          className={cs('justify-self--end', 'z-10')}
        />
        <CopyContent className={cs('align-self--center')}>
          <span>
            <TitleTheme
              data-part="title"
              title={titleP}
              css={{ marginBottom: '1rem' }}
            />
          </span>
          <div data-part="description">{copy}</div>
        </CopyContent>
        <div>{detailedAction}</div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};

export const SixtyFortyColumnV2 = Object.assign(RenderSixtyFortyColumnV2, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    image: {
      path: ['requires', 'hero', 'url'],
      control: 'image',
      selector: '[data-part="image"]',
      placement: 'top-end',
      options: {
        helpLabel: 'Recommended size: 700x700px; < 250kB',
      },
    },
    primaryAction: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
